import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { TasasRepository } from '@app/domain/ports/tpv/tasas.repository';
import {
  TASA_EJES,
  TasaEje,
  TasaPatchBody,
  TasaPatchResponse,
  TasaRead,
  TasasByEje,
} from '@app/data/dtos/tpv/tasas.dto';

/**
 * Piso (nivel superior) para una pantalla de tasas: siempre viene del GET del
 * nivel de arriba. La pantalla pinta el número como "piso" solo para lectura;
 * jamás lo revela el `detail` del backend (RN-11).
 *
 *  - `unidad`   → piso = tasas de EMPRESA.
 *  - `comercio` → piso = tasas de UNIDAD.
 *  - `empresa`  → no aplica piso (no hay nivel superior).
 */
export interface TasaPiso {
  /** Tasas del nivel superior (string 2 decimales o null; null = 0 %). */
  tasas: TasasByEje;
}

/**
 * Caso de uso: tasas de comisión por eje (contrato §1).
 *
 * Clase pura de TypeScript — sin `@Injectable` ni `inject()` (CLAUDE.md:
 * "Domain layer has ZERO Angular imports"). La inyección se configura en
 * `tpv.providers.ts` con `useFactory` para resolver `TasasRepository`.
 *
 * Delega los 6 métodos GET/PATCH al repositorio y ofrece dos helpers de UI:
 *  - `getUnitTasaWithPiso`     → carga UN + piso (empresa) en una sola llamada.
 *  - `getMerchantTasaWithPiso` → carga comercio + piso (unidad) en una sola llamada.
 * Ambos hacen `forkJoin` para no encadenar suscripciones en el contenedor.
 */
export class TasasUseCase {
  constructor(private readonly repository: TasasRepository) {}

  // ── Empresa (Tasa Banco) ───────────────────────────────────────────────────
  getCompanyTasa(idCompany: string): Observable<TasaRead | null> {
    return this.repository.getCompanyTasa(idCompany);
  }
  updateCompanyTasa(idCompany: string, body: TasaPatchBody): Observable<TasaPatchResponse> {
    return this.repository.updateCompanyTasa(idCompany, body);
  }

  // ── Unidad (Tasa Reseller) ─────────────────────────────────────────────────
  getUnitTasa(idCompany: string, idUnit: string): Observable<TasaRead | null> {
    return this.repository.getUnitTasa(idCompany, idUnit);
  }
  updateUnitTasa(
    idCompany: string,
    idUnit: string,
    body: TasaPatchBody,
  ): Observable<TasaPatchResponse> {
    return this.repository.updateUnitTasa(idCompany, idUnit, body);
  }

  // ── Comercio (Tasa Comercio) ───────────────────────────────────────────────
  getMerchantTasa(idMerchant: string): Observable<TasaRead | null> {
    return this.repository.getMerchantTasa(idMerchant);
  }
  updateMerchantTasa(idMerchant: string, body: TasaPatchBody): Observable<TasaPatchResponse> {
    return this.repository.updateMerchantTasa(idMerchant, body);
  }

  // ── Helpers combinados (contenedor + piso) ─────────────────────────────────
  /**
   * Unidad + su piso (empresa). Un solo `forkJoin`: la UI arma el read model
   * con `TasaRead` de la UN y las tasas de empresa como piso client-side.
   */
  getUnitTasaWithPiso(
    idCompany: string,
    idUnit: string,
  ): Observable<{ unit: TasaRead | null; piso: TasaPiso }> {
    return forkJoin({
      unit: this.repository.getUnitTasa(idCompany, idUnit),
      empresa: this.repository.getCompanyTasa(idCompany),
    }).pipe(map(({ unit, empresa }) => ({ unit, piso: { tasas: empresa?.tasas ?? {} } })));
  }

  /**
   * Comercio + su piso (unidad). Requiere `idCompany` + `idUnit` (dueños de la
   * UN a la que pertenece el comercio) para poder consultar el nivel superior.
   */
  getMerchantTasaWithPiso(
    idMerchant: string,
    idCompany: string,
    idUnit: string,
  ): Observable<{ merchant: TasaRead | null; piso: TasaPiso }> {
    return forkJoin({
      merchant: this.repository.getMerchantTasa(idMerchant),
      unit: this.repository.getUnitTasa(idCompany, idUnit),
    }).pipe(map(({ merchant, unit }) => ({ merchant, piso: { tasas: unit?.tasas ?? {} } })));
  }

  /**
   * Presentación: convierte el mapa del GET (string 2 dec o null) a números
   * porcentaje. `null` → 0 (RN-05: "eje sin configurar → 0 %").
   * NO usar para volver a mandar al backend: si un eje era null, quedaría en 0
   * y el backend violaría el piso del nivel superior (RN-05).
   */
  static tasasToPercent(tasas: TasasByEje | undefined): Record<TasaEje, number> {
    const out: Record<TasaEje, number> = { credito: 0, debito: 0, internacional: 0, amex: 0 };
    if (!tasas) return out;
    for (const eje of TASA_EJES) {
      const raw = tasas[eje];
      const n = raw == null ? 0 : Number(raw);
      out[eje] = Number.isFinite(n) ? n : 0;
    }
    return out;
  }
}
